import React, { useState, useEffect, useMemo } from "react";
import { ArrowBack, Assessment, CalendarToday, TrendingUp, Receipt, Storage, CheckCircleOutline, ErrorOutline, TrendingDown } from "@material-ui/icons";
import { useHistory } from "react-router-dom";
import styled, { keyframes } from "styled-components";
import { Chart } from "react-google-charts";
import fetchClient from "../../api/fetchClient";
import Loader from "../../components/Loader";
import message from "../../helper/message";
import { formatAbbrValue } from "../../helper/formatValue";
import {
    Container,
    ModernHeader,
    HeaderLeft,
    HeaderContent,
    PageTitle,
    PageSubtitle,
    BackButton,
    ResultsCard,
    getTheme,
} from "../../components/common";

function DashboardConcur() {
    const history = useHistory();
    const theme = getTheme('concur');
    const [isLoading, setIsLoading] = useState(false);
    const [metrics, setMetrics] = useState([]);
    const [concurs, setConcurs] = useState([]);
    const [aplicacoes, setAplicacoes] = useState([]);

    // Filtros
    const [selectedConcur, setSelectedConcur] = useState('');
    const [selectedYear, setSelectedYear] = useState('');
    const [availableYears, setAvailableYears] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [typeChartMode, setTypeChartMode] = useState('count');
    const [dbChartMode, setDbChartMode] = useState('count');
    const [volBasePage, setVolBasePage] = useState(1);
    const [volBaseRowsPerPage, setVolBaseRowsPerPage] = useState(10);
    const [selectedMonth, setSelectedMonth] = useState("");
    const [selectedCurrency, setSelectedCurrency] = useState("");

    const formatCurrency = (value, currency) => {
        try {
            return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: currency || 'BRL' }).format(value || 0);
        } catch {
            return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0);
        }
    };

    const formatAbbrCurrency = (value, currency) => {
        const cur = currency || 'BRL';
        const abs = Math.abs(value || 0);
        let abbreviated;
        if (abs >= 1_000_000_000) {
            abbreviated = ((value || 0) / 1_000_000_000).toFixed(1).replace('.', ',') + 'B';
        } else if (abs >= 1_000_000) {
            abbreviated = ((value || 0) / 1_000_000).toFixed(1).replace('.', ',') + 'M';
        } else if (abs >= 1_000) {
            abbreviated = ((value || 0) / 1_000).toFixed(1).replace('.', ',') + 'K';
        } else {
            abbreviated = (value || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        }
        try {
            const symbol = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: cur })
                .formatToParts(0)
                .find(p => p.type === 'currency')?.value || cur;
            return `${symbol} ${abbreviated}`;
        } catch {
            return `R$ ${abbreviated}`;
        }
    };

    useEffect(() => {
        let isMounted = true;

        const fetchConcurs = async () => {
            try {
                let allData = [];
                let nextToken = null;
                do {
                    const { data } = await fetchClient.get(`/getAllConcur?limit=100${nextToken ? `&nextToken=${nextToken}` : ''}`);
                    allData = [...allData, ...data.value];
                    nextToken = data.nextToken;
                } while (nextToken);

                if (isMounted) {
                    setConcurs(allData);
                }
            } catch (error) {
                if (isMounted) {
                    console.error(error);
                    message.error("Erro ao buscar concurs");
                }
            }
        };

        fetchConcurs();

        return () => {
            isMounted = false;
        };
    }, []);

    useEffect(() => {
        let isMounted = true;
        const fetchAplicacoes = async () => {
            try {
                let allData = [];
                let nextToken = null;
                do {
                    const { data } = await fetchClient.get(`getAllAplicacao?limit=100${nextToken ? `&nextToken=${nextToken}` : ''}`);
                    allData = [...allData, ...data.value];
                    nextToken = data.nextToken;
                } while (nextToken);
                if (isMounted) setAplicacoes(allData);
            } catch (e) { /* silencioso */ }
        };
        fetchAplicacoes();
        return () => { isMounted = false; };
    }, []);

    useEffect(() => {
        let isMounted = true;

        const fetchMetrics = async () => {
            if (!selectedConcur) return;

            try {
                if (isMounted) {
                    setIsLoading(true);
                }

                // Buscar anos disponíveis primeiro (se ainda não tiver ano selecionado)
                let yearToUse = selectedYear;

                if (!yearToUse) {
                    // Fazer primeira requisição para descobrir anos disponíveis
                    const currentYear = new Date().getFullYear().toString();
                    const { data: initialData } = await fetchClient.get(
                        `/concur/expense-metrics?concur_id=${selectedConcur}&year=${currentYear}`
                    );

                    // Extrair anos únicos dos dados
                    const years = new Set();
                    initialData.forEach(item => {
                        if (item.year) years.add(item.year.toString());
                    });

                    // Se não encontrou dados no ano atual, tentar buscar dados gerais
                    if (years.size === 0) {
                        // Tentar anos anteriores
                        for (let y = currentYear; y >= currentYear - 5; y--) {
                            try {
                                const { data: yearData } = await fetchClient.get(
                                    `/concur/expense-metrics?concur_id=${selectedConcur}&year=${y}`
                                );
                                yearData.forEach(item => {
                                    if (item.year) years.add(item.year.toString());
                                });
                                if (years.size > 0) break;
                            } catch (e) {
                                console.log(`No data for year ${y}`);
                            }
                        }
                    }

                    const yearsArray = Array.from(years).sort((a, b) => b - a);
                    if (isMounted) {
                        setAvailableYears(yearsArray);
                        if (yearsArray.length > 0) {
                            yearToUse = yearsArray[0];
                            setSelectedYear(yearToUse);
                        }
                    }
                }

                if (yearToUse) {
                    const { data } = await fetchClient.get(
                        `/concur/expense-metrics?concur_id=${selectedConcur}&year=${yearToUse}${selectedCurrency ? `&currency=${selectedCurrency}` : ''}`
                    );

                    if (isMounted) {
                        // Extrair anos dos dados para atualizar lista
                        const years = new Set();
                        data.forEach(item => {
                            if (item.year) years.add(item.year.toString());
                        });
                        const yearsArray = Array.from(years).sort((a, b) => b - a);
                        if (yearsArray.length > 0 && availableYears.length === 0) {
                            setAvailableYears(yearsArray);
                        }

                        setMetrics(data);
                    }
                }
            } catch (error) {
                if (isMounted) {
                    console.error(error);
                    message.error(error.response?.data?.message || "Erro ao buscar métricas");
                    setMetrics([]);
                }
            } finally {
                if (isMounted) {
                    setIsLoading(false);
                }
            }
        };

        if (selectedConcur) {
            fetchMetrics();
        }

        return () => {
            isMounted = false;
        };
    }, [selectedConcur, selectedYear, selectedCurrency]);

    // Preparar dados para o gráfico de evolução mensal
    const prepareEvolutionChartData = () => {
        if (!filteredMetrics || filteredMetrics.length === 0) {
            return [["Mês", "Documentos"]];
        }

        const monthNames = ["Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez"];

        // Agregar dados por mês
        const monthlyData = {};

        filteredMetrics.forEach(metric => {
            const month = metric.month;
            if (!monthlyData[month]) {
                monthlyData[month] = 0;
            }
            monthlyData[month] += metric.documentCount || 0;
        });

        const chartData = [["Mês", "Documentos"]];

        Object.keys(monthlyData)
            .map(m => parseInt(m))
            .sort((a, b) => a - b)
            .forEach(month => {
                const monthIndex = month - 1;
                const monthName = monthNames[monthIndex];
                chartData.push([monthName, monthlyData[month]]);
            });

        return chartData;
    };

    // Preparar dados para o gráfico por tipo de documento
    const prepareTypeChartData = () => {
        if (!filteredMetrics || filteredMetrics.length === 0) {
            return [["Tipo", "Quantidade"]];
        }

        const typeData = {};

        filteredMetrics.forEach(metric => {
            const type = metric.tipoDocument || 'Outros';
            if (!typeData[type]) {
                typeData[type] = 0;
            }
            typeData[type] += metric.documentCount || 0;
        });

        const chartData = [["Tipo", "Quantidade"]];

        Object.entries(typeData).forEach(([type, count]) => {
            const label = type === 'expense' ? 'Expense' : type === 'cash' ? 'Cash' : type;
            chartData.push([label, count]);
        });

        return chartData;
    };

    // Preparar dados para o gráfico por base/database
    const prepareDatabaseChartData = () => {
        if (!filteredMetrics || filteredMetrics.length === 0) {
            return [["Base", "Documentos"]];
        }

        const dbData = {};

        filteredMetrics.forEach(metric => {
            const db = metric.database || 'Outros';
            if (!dbData[db]) {
                dbData[db] = 0;
            }
            dbData[db] += metric.documentCount || 0;
        });

        const chartData = [["Base", "Documentos"]];

        Object.entries(dbData)
            .sort((a, b) => b[1] - a[1])
            .forEach(([db, count]) => {
                chartData.push([db, count]);
            });

        return chartData;
    };

    // Preparar dados para o gráfico de valores por tipo
    const prepareValueChartData = () => {
        if (!singleCurrencyMetrics || singleCurrencyMetrics.length === 0) {
            return [["Mês", "Expense", "Cash"]];
        }

        const monthNames = ["Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez"];

        // Agregar dados por mês e tipo
        const monthlyData = {};

        // Multi-currency: one series per "type (currency)" combo
        const isMultiEvol = uniqueCurrencies.length > 1;
        if (isMultiEvol) {
            const typeLabel = (t) => t === 'expense' ? 'Expense' : t === 'cash' ? 'Cash' : (t || 'Outros');
            const combos = {};
            filteredMetrics.forEach(metric => {
                const month = metric.month;
                const curr = metric.currency || 'BRL';
                const key = `${typeLabel(metric.tipoDocument)} (${curr})`;
                if (!combos[month]) combos[month] = {};
                combos[month][key] = (combos[month][key] || 0) + (metric.totalAmount || 0);
            });
            const seriesKeys = [...new Set(
                filteredMetrics.map(m => `${typeLabel(m.tipoDocument)} (${m.currency || 'BRL'})`)
            )].sort();
            const chartData = [["Mês", ...seriesKeys]];
            Object.keys(combos).map(Number).sort((a, b) => a - b).forEach(month => {
                chartData.push([monthNames[month - 1], ...seriesKeys.map(k => combos[month][k] || 0)]);
            });
            return chartData;
        }

        singleCurrencyMetrics.forEach(metric => {
            const month = metric.month;
            const type = metric.tipoDocument;

            if (!monthlyData[month]) {
                monthlyData[month] = {
                    expense: 0,
                    cash: 0
                };
            }

            if (type === 'expense') {
                monthlyData[month].expense += metric.totalAmount || 0;
            } else if (type === 'cash') {
                monthlyData[month].cash += metric.totalAmount || 0;
            }
        });

        const chartData = [["Mês", "Expense", "Cash"]];

        Object.keys(monthlyData)
            .map(m => parseInt(m))
            .sort((a, b) => a - b)
            .forEach(month => {
                const monthIndex = month - 1;
                const monthName = monthNames[monthIndex];
                chartData.push([
                    monthName,
                    monthlyData[month].expense,
                    monthlyData[month].cash
                ]);
            });

        return chartData;
    };

    // Preparar dados para o gráfico por tipo - valor
    const prepareTypeValueData = () => {
        if (!filteredMetrics || filteredMetrics.length === 0) {
            return [["Tipo", "Valor"]];
        }
        const isMulti = uniqueCurrencies.length > 1;
        const typeData = {};
        filteredMetrics.forEach(metric => {
            const type = metric.tipoDocument || 'Outros';
            const curr = metric.currency || 'BRL';
            const typeLabel = type === 'expense' ? 'Expense' : type === 'cash' ? 'Cash' : type;
            const label = isMulti ? `${typeLabel} (${curr})` : typeLabel;
            if (!typeData[label]) typeData[label] = 0;
            typeData[label] += metric.totalAmount || 0;
        });
        const header = isMulti ? "Valor" : `Valor (${chartValueCurrency})`;
        const chartData = [["Tipo", header]];
        Object.entries(typeData).forEach(([label, value]) => chartData.push([label, value]));
        return chartData;
    };

    // Preparar dados para o gráfico por base - valor
    const prepareDatabaseValueData = () => {
        if (!filteredMetrics || filteredMetrics.length === 0) {
            return [["Base", "Valor"]];
        }
        const isMulti = uniqueCurrencies.length > 1;
        const dbData = {};
        filteredMetrics.forEach(metric => {
            const db = metric.database || 'Outros';
            const curr = metric.currency || 'BRL';
            const label = isMulti ? `${db} (${curr})` : db;
            if (!dbData[label]) dbData[label] = 0;
            dbData[label] += metric.totalAmount || 0;
        });
        const header = isMulti ? "Valor" : `Valor (${chartValueCurrency})`;
        const chartData = [["Base", header]];
        Object.entries(dbData)
            .sort((a, b) => b[1] - a[1])
            .forEach(([label, value]) => chartData.push([label, value]));
        return chartData;
    };

    // Preparar dados para o gráfico por empresa (quando "all" está selecionado)
    const prepareCompanyChartData = () => {
        if (!filteredMetrics || filteredMetrics.length === 0 || selectedConcur !== 'all') {
            return [["Empresa", "Documentos"]];
        }

        const companyData = {};

        filteredMetrics.forEach(metric => {
            const company = metric.database || metric.companyId || 'Outros';
            if (!companyData[company]) {
                companyData[company] = 0;
            }
            companyData[company] += metric.documentCount || 0;
        });

        const chartData = [["Empresa", "Documentos"]];

        Object.entries(companyData)
            .sort((a, b) => b[1] - a[1])
            .forEach(([company, count]) => {
                chartData.push([company, count]);
            });

        return chartData;
    };

    // Calcular totalizadores
    const calculateTotals = () => {
        if (!filteredMetrics || filteredMetrics.length === 0) {
            return {
                documentCount: 0,
                totalAmount: 0,
                averagePerMonth: 0,
                databases: 0
            };
        }

        const documentCount = filteredMetrics.reduce((sum, m) => sum + (m.documentCount || 0), 0);
        const totalAmount = filteredMetrics.reduce((sum, m) => sum + (m.totalAmount || 0), 0);

        // Contar meses únicos
        const uniqueMonths = new Set(filteredMetrics.map(m => m.month));
        const monthsWithData = uniqueMonths.size;

        // Contar databases únicos
        const uniqueDatabases = new Set(filteredMetrics.map(m => m.database).filter(Boolean));

        return {
            documentCount,
            totalAmount,
            averagePerMonth: monthsWithData > 0 ? Math.round(documentCount / monthsWithData) : 0,
            databases: uniqueDatabases.size
        };
    };

    const uniqueCurrencies = useMemo(() => {
        return [...new Set(metrics.map(m => m.currency).filter(Boolean))].sort();
    }, [metrics]);

    const filteredMetrics = useMemo(() => {
        if (!selectedMonth) return metrics;
        return metrics.filter(m => parseInt(m.month) === parseInt(selectedMonth));
    }, [metrics, selectedMonth]);

    // Currency used by value-mode charts: explicit selection OR first available
    const chartValueCurrency = useMemo(
        () => selectedCurrency || uniqueCurrencies[0] || "BRL",
        [selectedCurrency, uniqueCurrencies]
    );

    // Metrics restricted to a single currency for value-mode charts
    const singleCurrencyMetrics = useMemo(
        () => filteredMetrics.filter(m => m.currency === chartValueCurrency),
        [filteredMetrics, chartValueCurrency]
    );

    const kpisByCurrency = useMemo(() => {
        const byCurrency = {};
        filteredMetrics.forEach(m => {
            const curr = m.currency || 'BRL';
            if (!byCurrency[curr]) byCurrency[curr] = { documentCount: 0, totalAmount: 0 };
            byCurrency[curr].documentCount += m.documentCount || 0;
            byCurrency[curr].totalAmount += m.totalAmount || 0;
        });
        return Object.entries(byCurrency)
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([currency, data]) => ({ currency, ...data }));
    }, [filteredMetrics]);

    const isMultiCurrency = kpisByCurrency.length > 1;

    const kpiVariation = useMemo(() => {
        const pct = (curr, prev) => (prev > 0 ? ((curr - prev) / prev) * 100 : null);
        if (!metrics.length) return { docs: null, value: null };
        const grouped = {};
        metrics.forEach((m) => {
            const mo = parseInt(m.month);
            if (!grouped[mo]) grouped[mo] = { documentCount: 0, totalAmount: 0 };
            grouped[mo].documentCount += m.documentCount || 0;
            grouped[mo].totalAmount += m.totalAmount || 0;
        });
        const months = Object.keys(grouped).map(Number).sort((a, b) => a - b);
        if (months.length < 2) return { docs: null, value: null };
        const last = grouped[months[months.length - 1]];
        const prev = grouped[months[months.length - 2]];
        return {
            docs: pct(last.documentCount, prev.documentCount),
            value: pct(last.totalAmount, prev.totalAmount),
        };
    }, [metrics]);

    const sortedMetrics = useMemo(() => {
        return [...filteredMetrics].sort((a, b) => {
            if (a.database !== b.database) {
                return (a.database || '').localeCompare(b.database || '');
            }
            if (a.month !== b.month) return a.month - b.month;
            return (a.tipoDocument || '').localeCompare(b.tipoDocument || '');
        });
    }, [filteredMetrics]);

    const totalPages = Math.max(1, Math.ceil(sortedMetrics.length / rowsPerPage));
    const paginatedMetrics = sortedMetrics.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage);

    // ── Volumetria: configurado vs realizado ──────────────────
    const MESES_ABREV = ["Jan","Fev","Mar","Abr","Mai","Jun","Jul","Ago","Set","Out","Nov","Dez"];

    const currentConcurObj = useMemo(() => {
        if (!selectedConcur || selectedConcur === 'all') return null;
        return concurs.find(c => c.id === selectedConcur) || null;
    }, [selectedConcur, concurs]);

    // vol-real entries for the selected year (all months)
    const volRealEntries = useMemo(() => {
        if (!selectedYear) return [];
        if (selectedConcur === 'all') {
            return concurs.flatMap(c =>
                (c['volumetria-real'] || []).filter(e => String(e.ano) === String(selectedYear))
            );
        }
        if (!currentConcurObj) return [];
        return (currentConcurObj['volumetria-real'] || []).filter(
            e => String(e.ano) === String(selectedYear)
        );
    }, [currentConcurObj, selectedYear, selectedConcur, concurs]);

    // actual metrics aggregated by month + tipo (uses ALL months, ignores selectedMonth filter)
    const actualByMonthType = useMemo(() => {
        const result = {};
        metrics.forEach(m => {
            const mo = Number(m.month);
            if (!result[mo]) result[mo] = { expense: 0, cash: 0 };
            const tipo = (m.tipoDocument || '').toLowerCase();
            if (tipo === 'expense') result[mo].expense += m.documentCount || 0;
            else if (tipo === 'cash') result[mo].cash   += m.documentCount || 0;
        });
        return result;
    }, [metrics]);

    // map: empresaId → database (para cruzar volumetria-real com métricas)
    const empresaIdToDatabase = useMemo(() => {
        const map = {};
        aplicacoes.forEach(a => { if (a.id && a.database) map[a.id] = a.database; });
        return map;
    }, [aplicacoes]);

    // actual metrics aggregated by database + tipo (for the base-level confronto table)
    const actualByDbType = useMemo(() => {
        const result = {};
        metrics
            .filter(m => !selectedMonth || Number(m.month) === Number(selectedMonth))
            .forEach(m => {
                const db = m.database || 'Outros';
                if (!result[db]) result[db] = { expense: 0, cash: 0 };
                const tipo = (m.tipoDocument || '').toLowerCase();
                if (tipo === 'expense') result[db].expense += m.documentCount || 0;
                else if (tipo === 'cash') result[db].cash   += m.documentCount || 0;
            });
        return result;
    }, [metrics, selectedMonth]);

    // vol-real aggregated by month (sum all bases)
    const volByMonth = useMemo(() => {
        const result = {};
        const src = selectedMonth
            ? volRealEntries.filter(e => Number(e.mes) === Number(selectedMonth))
            : volRealEntries;
        src.forEach(e => {
            const mo = Number(e.mes);
            if (!result[mo]) result[mo] = { expense: 0, cash: 0 };
            result[mo].expense += Number(e.expense) || 0;
            result[mo].cash    += Number(e.cash)    || 0;
        });
        return result;
    }, [volRealEntries, selectedMonth]);

    // vol-real aggregated by base (empresaNome), for selected month or all months
    const volByBase = useMemo(() => {
        const result = {};
        const src = selectedMonth
            ? volRealEntries.filter(e => Number(e.mes) === Number(selectedMonth))
            : volRealEntries;
        src.forEach(e => {
            const base = e.empresaNome || e.empresaId;
            if (!result[base]) result[base] = { expense: 0, cash: 0 };
            result[base].expense += Number(e.expense) || 0;
            result[base].cash    += Number(e.cash)    || 0;
        });
        return result;
    }, [volRealEntries, selectedMonth]);

    // totals for KPI cards
    const volTotals = useMemo(() => {
        const cfgExpense = Object.values(volByMonth).reduce((a, v) => a + v.expense, 0);
        const cfgCash    = Object.values(volByMonth).reduce((a, v) => a + v.cash,    0);
        const realExpense = selectedMonth
            ? (actualByMonthType[Number(selectedMonth)]?.expense || 0)
            : Object.values(actualByMonthType).reduce((a, v) => a + v.expense, 0);
        const realCash = selectedMonth
            ? (actualByMonthType[Number(selectedMonth)]?.cash || 0)
            : Object.values(actualByMonthType).reduce((a, v) => a + v.cash, 0);
        const pctExpense = cfgExpense > 0 ? (realExpense / cfgExpense) * 100 : null;
        const pctCash    = cfgCash    > 0 ? (realCash    / cfgCash)    * 100 : null;
        return { cfgExpense, cfgCash, realExpense, realCash, pctExpense, pctCash };
    }, [volByMonth, actualByMonthType, selectedMonth]);

    // chart data: configured vs real per month (ColumnChart grouped)
    const volComparisonChartData = useMemo(() => {
        if (!volRealEntries.length) return null;
        const allMonths = [...new Set([
            ...Object.keys(volByMonth).map(Number),
            ...Object.keys(actualByMonthType).map(Number),
        ])].sort((a, b) => a - b);
        if (allMonths.length === 0) return null;
        const data = [["Mês", "Config. Expense", "Real Expense", "Config. Cash", "Real Cash"]];
        allMonths.forEach(mes => {
            const vol  = volByMonth[mes]         || { expense: 0, cash: 0 };
            const real = actualByMonthType[mes]  || { expense: 0, cash: 0 };
            data.push([MESES_ABREV[mes - 1], vol.expense, real.expense, vol.cash, real.cash]);
        });
        return data.length > 1 ? data : null;
    }, [volRealEntries, volByMonth, actualByMonthType]);

    // rows for per-base table — only bases with configured volumetria (linked in concur)
    const volBaseRows = useMemo(() => {
        const bases = Object.keys(volByBase);
        return [...bases].sort().map(base => {
            // encontrar o empresaId desta base para resolver o database correto
            const volEntry = volRealEntries.find(e => (e.empresaNome || e.empresaId) === base);
            const database = (volEntry && empresaIdToDatabase[volEntry.empresaId]) || base;
            const cfg  = volByBase[base]    || { expense: 0, cash: 0 };
            const real = actualByDbType[database] || { expense: 0, cash: 0 };
            const pctE = cfg.expense > 0 ? (real.expense / cfg.expense) * 100 : null;
            const pctC = cfg.cash    > 0 ? (real.cash    / cfg.cash)    * 100 : null;
            return { base, cfg, real, pctE, pctC };
        });
    }, [volByBase, actualByDbType, volRealEntries, empresaIdToDatabase]);

    useEffect(() => {
        setCurrentPage(1);
    }, [metrics, rowsPerPage, selectedMonth]);

    useEffect(() => {
        setVolBasePage(1);
    }, [volBaseRows, volBaseRowsPerPage]);

    const volBaseTotalPages = Math.max(1, Math.ceil(volBaseRows.length / volBaseRowsPerPage));
    const paginatedVolBaseRows = volBaseRows.slice((volBasePage - 1) * volBaseRowsPerPage, volBasePage * volBaseRowsPerPage);

    const totals = calculateTotals();
    const selectedConcurName = selectedConcur === 'all'
        ? 'Todos os Concurs'
        : concurs.find(c => c.id === selectedConcur)?.description || '';

    const evolutionChartOptions = {
        title: `Evolução Mensal de Documentos${selectedYear ? ` - ${selectedYear}` : ''}`,
        chartArea: { width: "80%", height: "70%" },
        hAxis: {
            title: "Mês",
            titleTextStyle: { color: "#2c3e50", fontSize: 14, bold: true },
        },
        vAxis: {
            title: "Quantidade de Documentos",
            titleTextStyle: { color: "#2c3e50", fontSize: 14, bold: true },
            minValue: 0,
        },
        colors: ["#8e44ad"],
        legend: { position: "none" },
        animation: {
            startup: true,
            duration: 1000,
            easing: "out",
        },
    };

    const typeChartOptions = {
        title: `Documentos por Tipo${selectedYear ? ` - ${selectedYear}` : ''}`,
        pieHole: 0.4,
        colors: ["#8e44ad", "#9b59b6", "#3498db"],
        legend: { position: "bottom" },
        animation: {
            startup: true,
            duration: 1000,
            easing: "out",
        },
    };

    const databaseChartOptions = {
        title: `Documentos por Base${selectedYear ? ` - ${selectedYear}` : ''}`,
        chartArea: { width: "70%", height: "70%" },
        hAxis: {
            title: "Base",
            titleTextStyle: { color: "#2c3e50", fontSize: 14, bold: true },
        },
        vAxis: {
            title: "Quantidade de Documentos",
            titleTextStyle: { color: "#2c3e50", fontSize: 14, bold: true },
            minValue: 0,
        },
        colors: ["#9b59b6"],
        legend: { position: "none" },
        animation: {
            startup: true,
            duration: 1000,
            easing: "out",
        },
    };

    const valueChartOptions = {
        title: `Valor por Tipo de Documento${selectedYear ? ` - ${selectedYear}` : ''}`,
        chartArea: { width: "75%", height: "70%" },
        hAxis: {
            title: "Mês",
            titleTextStyle: { color: "#2c3e50", fontSize: 14, bold: true },
        },
        vAxis: {
            title: uniqueCurrencies.length > 1 ? "Valor" : `Valor (${chartValueCurrency})`,
            titleTextStyle: { color: "#2c3e50", fontSize: 14, bold: true },
            minValue: 0,
            format: "decimal",
        },
        colors: ["#8e44ad", "#3498db"],
        legend: { position: "top" },
        isStacked: true,
        animation: {
            startup: true,
            duration: 1000,
            easing: "out",
        },
    };

    const exportCSV = () => {
        const BOM = "\uFEFF";
        const headers = "Base;Tipo;Moeda;Mês;Ano;Documentos;Valor Total;Última Atualização";
        const rows = sortedMetrics.map(metric => {
            const tipoLabel = metric.tipoDocument === 'expense' ? 'Expense' :
                             metric.tipoDocument === 'cash' ? 'Cash' : metric.tipoDocument || '-';
            return [
                metric.database || '-',
                tipoLabel,
                metric.currency || 'BRL',
                (metric.month || '').toString(),
                (metric.year || selectedYear || '').toString(),
                (metric.documentCount || 0).toString(),
                (metric.totalAmount || 0).toString(),
                metric.lastUpdated || '',
            ].join(";");
        });
        const csv = BOM + headers + "\n" + rows.join("\n");
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `concur-dashboard${selectedYear ? '-' + selectedYear : ''}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };

    if (isLoading && !metrics.length) return <Loader />;

    return (
        <Container>
            <ModernHeader>
                <HeaderLeft>
                    <BackButton color={theme.primary} onClick={() => history.goBack()}>
                        <ArrowBack />
                    </BackButton>
                    <HeaderContent>
                        <PageTitle>
                            <Assessment style={{ marginRight: '12px', fontSize: '32px' }} />
                            Dashboard Concur
                        </PageTitle>
                        <PageSubtitle>Visualize métricas de despesas por período</PageSubtitle>
                    </HeaderContent>
                </HeaderLeft>
            </ModernHeader>

            <ResultsCard>
                <FiltersCard>
                    <FilterTitle>Filtros</FilterTitle>
                    <FiltersContainer>
                        <FilterGroup>
                            <FilterLabel>Concur</FilterLabel>
                            <FilterSelect
                                value={selectedConcur}
                                onChange={(e) => {
                                    setSelectedConcur(e.target.value);
                                    setSelectedYear('');
                                    setAvailableYears([]);
                                    setSelectedMonth('');
                                    setSelectedCurrency('');
                                }}
                            >
                                <option value="">Selecione um Concur</option>
                                <option value="all">Todos os Concurs</option>
                                {concurs.map(concur => (
                                    <option key={concur.id} value={concur.id}>
                                        {concur.description}
                                    </option>
                                ))}
                            </FilterSelect>
                        </FilterGroup>

                        {selectedConcur && (
                            <FilterGroup>
                                <FilterLabel>Ano</FilterLabel>
                                <FilterSelect
                                    value={selectedYear}
                                    onChange={(e) => { setSelectedYear(e.target.value); setSelectedMonth(''); setSelectedCurrency(''); }}
                                    disabled={availableYears.length === 0 || isLoading}
                                >
                                    {isLoading ? (
                                        <option value="">Carregando...</option>
                                    ) : availableYears.length === 0 ? (
                                        <option value="">Sem dados</option>
                                    ) : (
                                        availableYears.map(year => (
                                            <option key={year} value={year}>{year}</option>
                                        ))
                                    )}
                                </FilterSelect>
                            </FilterGroup>
                        )}

                        {selectedConcur && selectedYear && (
                            <FilterGroup>
                                <FilterLabel>Mês</FilterLabel>
                                <FilterSelect
                                    value={selectedMonth}
                                    onChange={(e) => setSelectedMonth(e.target.value)}
                                >
                                    <option value="">Todos</option>
                                    {["Janeiro","Fevereiro","Março","Abril","Maio","Junho","Julho","Agosto","Setembro","Outubro","Novembro","Dezembro"].map((m, i) => {
                                        if (parseInt(selectedYear) === new Date().getFullYear() && i + 1 > new Date().getMonth() + 1) return null;
                                        return <option key={i + 1} value={i + 1}>{m}</option>;
                                    })}
                                </FilterSelect>
                            </FilterGroup>
                        )}

                        {selectedConcur && selectedYear && uniqueCurrencies.length > 1 && (
                            <FilterGroup>
                                <FilterLabel>Moeda</FilterLabel>
                                <FilterSelect
                                    value={selectedCurrency}
                                    onChange={(e) => setSelectedCurrency(e.target.value)}
                                >
                                    <option value="">Todas as moedas</option>
                                    {uniqueCurrencies.map(c => (
                                        <option key={c} value={c}>{c}</option>
                                    ))}
                                </FilterSelect>
                            </FilterGroup>
                        )}
                    </FiltersContainer>
                </FiltersCard>

                {selectedConcur && (
                    <>
                        {isMultiCurrency ? (
                            <TotalizersGrid>
                                {kpisByCurrency.map(kpi => (
                                    <CurrencyKPICard key={kpi.currency}>
                                        <CurrencyKPIHeader>
                                            <CurrencyBadge>{kpi.currency}</CurrencyBadge>
                                            <span>{kpi.documentCount.toLocaleString('pt-BR')} documentos</span>
                                        </CurrencyKPIHeader>
                                        <CurrencyKPIValue>{formatAbbrCurrency(kpi.totalAmount, kpi.currency)}</CurrencyKPIValue>
                                        <CurrencyKPIDetail>Valor Total</CurrencyKPIDetail>
                                    </CurrencyKPICard>
                                ))}
                                <TotalizerCard color="#3498db">
                                    <TotalizerIcon color="#3498db">
                                        <CalendarToday />
                                    </TotalizerIcon>
                                    <TotalizerContent>
                                        <TotalizerValue>{formatAbbrValue(totals.averagePerMonth)}</TotalizerValue>
                                        <TotalizerLabel>Média por Mês</TotalizerLabel>
                                    </TotalizerContent>
                                </TotalizerCard>
                                <TotalizerCard color="#f39c12">
                                    <TotalizerIcon color="#f39c12">
                                        <Storage />
                                    </TotalizerIcon>
                                    <TotalizerContent>
                                        <TotalizerValue>{formatAbbrValue(totals.databases)}</TotalizerValue>
                                        <TotalizerLabel>Bases de Dados</TotalizerLabel>
                                    </TotalizerContent>
                                </TotalizerCard>
                            </TotalizersGrid>
                        ) : (
                            <TotalizersGrid>
                                <TotalizerCard color="#8e44ad">
                                    <TotalizerIcon color="#8e44ad">
                                        <Receipt />
                                    </TotalizerIcon>
                                    <TotalizerContent>
                                        <TotalizerValue>{formatAbbrValue(totals.documentCount)}</TotalizerValue>
                                        <TotalizerLabel>Total de Documentos</TotalizerLabel>
                                        {!selectedMonth && kpiVariation.docs !== null && (
                                            <VariationBadge positive={kpiVariation.docs >= 0}>
                                                {kpiVariation.docs >= 0 ? "▲" : "▼"} {Math.abs(kpiVariation.docs).toFixed(1)}% vs ant.
                                            </VariationBadge>
                                        )}
                                    </TotalizerContent>
                                </TotalizerCard>

                                <TotalizerCard color="#27ae60">
                                    <TotalizerIcon color="#27ae60">
                                        <TrendingUp />
                                    </TotalizerIcon>
                                    <TotalizerContent>
                                        <TotalizerValue>
                                            {kpisByCurrency.length === 1
                                                ? formatAbbrCurrency(kpisByCurrency[0].totalAmount, kpisByCurrency[0].currency)
                                                : formatAbbrValue(totals.totalAmount, true)}
                                        </TotalizerValue>
                                        <TotalizerLabel>Valor Total</TotalizerLabel>
                                        {!selectedMonth && kpiVariation.value !== null && (
                                            <VariationBadge positive={kpiVariation.value >= 0}>
                                                {kpiVariation.value >= 0 ? "▲" : "▼"} {Math.abs(kpiVariation.value).toFixed(1)}% vs ant.
                                            </VariationBadge>
                                        )}
                                    </TotalizerContent>
                                </TotalizerCard>

                                <TotalizerCard color="#3498db">
                                    <TotalizerIcon color="#3498db">
                                        <CalendarToday />
                                    </TotalizerIcon>
                                    <TotalizerContent>
                                        <TotalizerValue>{formatAbbrValue(totals.averagePerMonth)}</TotalizerValue>
                                        <TotalizerLabel>Média por Mês</TotalizerLabel>
                                    </TotalizerContent>
                                </TotalizerCard>

                                <TotalizerCard color="#f39c12">
                                    <TotalizerIcon color="#f39c12">
                                        <Storage />
                                    </TotalizerIcon>
                                    <TotalizerContent>
                                        <TotalizerValue>{formatAbbrValue(totals.databases)}</TotalizerValue>
                                        <TotalizerLabel>Bases de Dados</TotalizerLabel>
                                    </TotalizerContent>
                                </TotalizerCard>
                            </TotalizersGrid>
                        )}

                        {/* ── Volumetria: Configurado vs Realizado ─────────── */}
                        {selectedConcur && selectedYear && volRealEntries.length > 0 && (
                            <VolSection>
                                <VolSectionHeader>
                                    <VolSectionTitle>
                                        <Assessment style={{ fontSize: 20, marginRight: 8, color: '#8e44ad' }} />
                                        Volumetria — Configurado vs Realizado
                                        {selectedConcur === 'all' ? ' · Todos os Concurs' : ` · ${selectedConcurName}`}
                                        {' '}{selectedYear}
                                        {selectedMonth ? ` · ${["Janeiro","Fevereiro","Março","Abril","Maio","Junho","Julho","Agosto","Setembro","Outubro","Novembro","Dezembro"][Number(selectedMonth)-1]}` : " · Ano completo"}
                                    </VolSectionTitle>
                                </VolSectionHeader>

                                {/* KPI cards */}
                                <VolKpiRow>
                                    {[
                                        { label: "Expense", cfg: volTotals.cfgExpense, real: volTotals.realExpense, pct: volTotals.pctExpense, color: "#27ae60" },
                                        { label: "Cash",    cfg: volTotals.cfgCash,    real: volTotals.realCash,    pct: volTotals.pctCash,    color: "#e67e22" },
                                    ].map(({ label, cfg, real, pct, color }) => {
                                        const status = pct === null ? "nd" : pct > 100 ? "over" : pct >= 80 ? "warn" : "ok";
                                        const statusColor = { nd: "#94a3b8", over: "#e74c3c", warn: "#f39c12", ok: "#27ae60" }[status];
                                        return (
                                            <VolKpiCard key={label} statusColor={statusColor}>
                                                <VolKpiTop>
                                                    <VolKpiLabel>{label}</VolKpiLabel>
                                                    <VolKpiBadge statusColor={statusColor}>
                                                        {pct === null ? "S/D" : `${pct.toFixed(1)}%`}
                                                    </VolKpiBadge>
                                                </VolKpiTop>
                                                <VolKpiNumbers>
                                                    <VolKpiItem>
                                                        <VolKpiNum color={color}>{cfg.toLocaleString("pt-BR")}</VolKpiNum>
                                                        <VolKpiSub>Configurado</VolKpiSub>
                                                    </VolKpiItem>
                                                    <VolKpiDivider />
                                                    <VolKpiItem>
                                                        <VolKpiNum color={statusColor}>{real.toLocaleString("pt-BR")}</VolKpiNum>
                                                        <VolKpiSub>Realizado</VolKpiSub>
                                                    </VolKpiItem>
                                                    <VolKpiItem>
                                                        <VolKpiNum color={statusColor}>{cfg > 0 ? (cfg - real).toLocaleString("pt-BR") : "—"}</VolKpiNum>
                                                        <VolKpiSub>{real > cfg ? "Excedido" : "Saldo"}</VolKpiSub>
                                                    </VolKpiItem>
                                                </VolKpiNumbers>
                                                {pct !== null && (
                                                    <VolProgressBar>
                                                        <VolProgressFill pct={Math.min(pct, 100)} color={statusColor} />
                                                    </VolProgressBar>
                                                )}
                                            </VolKpiCard>
                                        );
                                    })}
                                </VolKpiRow>

                                {/* Comparison chart — hidden when single month selected */}
                                {!selectedMonth && volComparisonChartData && (
                                    <ChartCard>
                                        <Chart
                                            chartType="ColumnChart"
                                            width="100%"
                                            height="320px"
                                            data={volComparisonChartData}
                                            options={{
                                                title: `Configurado vs Realizado por Mês — ${selectedYear}`,
                                                chartArea: { width: "78%", height: "68%" },
                                                hAxis: { title: "Mês" },
                                                vAxis: { title: "Documentos", minValue: 0 },
                                                colors: ["#27ae60", "#82e0aa", "#e67e22", "#f0b27a"],
                                                legend: { position: "top" },
                                                animation: { startup: true, duration: 800, easing: "out" },
                                            }}
                                        />
                                    </ChartCard>
                                )}

                            </VolSection>
                        )}

                        {metrics.length > 0 ? (
                            <>
                                <ChartsRow>
                                    {!selectedMonth && (
                                        <ChartCard>
                                            <Chart
                                                chartType="LineChart"
                                                width="100%"
                                                height="350px"
                                                data={prepareEvolutionChartData()}
                                                options={evolutionChartOptions}
                                            />
                                        </ChartCard>
                                    )}

                                    <ChartCard>
                                        <ChartHeader>
                                            <ChartTitle>Por Tipo</ChartTitle>
                                            <ToggleGroup>
                                                <ToggleBtn active={typeChartMode === 'count'} color="#8e44ad" onClick={() => setTypeChartMode('count')}>Documentos</ToggleBtn>
                                                <ToggleBtn active={typeChartMode === 'value'} color="#8e44ad" onClick={() => setTypeChartMode('value')}>Valor</ToggleBtn>
                                            </ToggleGroup>
                                        </ChartHeader>
                                        <Chart
                                            chartType="PieChart"
                                            width="100%"
                                            height="320px"
                                            data={typeChartMode === 'count' ? prepareTypeChartData() : prepareTypeValueData()}
                                            options={{ ...typeChartOptions, title: typeChartMode === 'count' ? "Documentos por Tipo" : "Valor por Tipo" }}
                                        />
                                    </ChartCard>
                                </ChartsRow>

                                <ChartsRow>
                                    <ChartCard>
                                        <ChartHeader>
                                            <ChartTitle>Por Base</ChartTitle>
                                            <ToggleGroup>
                                                <ToggleBtn active={dbChartMode === 'count'} color="#8e44ad" onClick={() => setDbChartMode('count')}>Documentos</ToggleBtn>
                                                <ToggleBtn active={dbChartMode === 'value'} color="#8e44ad" onClick={() => setDbChartMode('value')}>Valor</ToggleBtn>
                                            </ToggleGroup>
                                        </ChartHeader>
                                        <Chart
                                            chartType="ColumnChart"
                                            width="100%"
                                            height="320px"
                                            data={dbChartMode === 'count' ? prepareDatabaseChartData() : prepareDatabaseValueData()}
                                            options={{
                                                ...databaseChartOptions,
                                                title: dbChartMode === 'count' ? "Documentos por Base" : "Valor por Base",
                                                vAxis: {
                                                    title: dbChartMode === 'count' ? "Quantidade de Documentos" : (uniqueCurrencies.length > 1 ? "Valor" : `Valor (${chartValueCurrency})`),
                                                    titleTextStyle: { color: "#2c3e50", fontSize: 14, bold: true },
                                                    minValue: 0,
                                                    ...(dbChartMode === 'value' ? { format: "decimal" } : {}),
                                                },
                                            }}
                                        />
                                    </ChartCard>

                                    {!selectedMonth && (
                                        <ChartCard>
                                            <Chart
                                                chartType="ColumnChart"
                                                width="100%"
                                                height="350px"
                                                data={prepareValueChartData()}
                                                options={valueChartOptions}
                                            />
                                        </ChartCard>
                                    )}
                                </ChartsRow>

                                {selectedConcur === 'all' && (
                                    <FullWidthChartCard>
                                        <Chart
                                            chartType="ColumnChart"
                                            width="100%"
                                            height="400px"
                                            data={prepareCompanyChartData()}
                                            options={{
                                                title: `Documentos por Empresa${selectedYear ? ` - ${selectedYear}` : ''}`,
                                                chartArea: { width: "70%", height: "70%" },
                                                hAxis: {
                                                    title: "Empresa",
                                                    titleTextStyle: { color: "#2c3e50", fontSize: 14, bold: true },
                                                },
                                                vAxis: {
                                                    title: "Quantidade de Documentos",
                                                    titleTextStyle: { color: "#2c3e50", fontSize: 14, bold: true },
                                                    minValue: 0,
                                                },
                                                colors: ["#8e44ad"],
                                                legend: { position: "none" },
                                                animation: {
                                                    startup: true,
                                                    duration: 1000,
                                                    easing: "out",
                                                },
                                            }}
                                        />
                                    </FullWidthChartCard>
                                )}

                                {/* Confronto por Base — abaixo dos gráficos */}
                                {volBaseRows.length > 0 && (
                                    <TableCard>
                                        <TableTitleRow>
                                            <TableTitle style={{ margin: 0 }}>
                                                Confronto por Base{selectedMonth ? ` — ${MESES_ABREV[Number(selectedMonth)-1]}/${selectedYear}` : ` — ${selectedYear}`}
                                            </TableTitle>
                                        </TableTitleRow>
                                        <TableWrapper>
                                            <DataTable>
                                                <thead>
                                                    <tr>
                                                        <TableHeader align="left">Base</TableHeader>
                                                        <TableHeader>Config. Expense</TableHeader>
                                                        <TableHeader>Real Expense</TableHeader>
                                                        <TableHeader>% Exp</TableHeader>
                                                        <TableHeader>Config. Cash</TableHeader>
                                                        <TableHeader>Real Cash</TableHeader>
                                                        <TableHeader>% Cash</TableHeader>
                                                        <TableHeader>Status</TableHeader>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {paginatedVolBaseRows.map(({ base, cfg, real, pctE, pctC }) => {
                                                        const overE = pctE !== null && pctE > 100;
                                                        const overC = pctC !== null && pctC > 100;
                                                        const statusOk = !overE && !overC && (pctE !== null || pctC !== null);
                                                        return (
                                                            <TableRow key={base}>
                                                                <TableCell align="left"><strong style={{ fontFamily: "monospace", fontSize: 13 }}>{base}</strong></TableCell>
                                                                <TableCell align="right"><MetricBadge color="#27ae60">{cfg.expense.toLocaleString("pt-BR")}</MetricBadge></TableCell>
                                                                <TableCell align="right"><MetricBadge color={overE ? "#e74c3c" : "#27ae60"}>{real.expense.toLocaleString("pt-BR")}</MetricBadge></TableCell>
                                                                <TableCell align="center">
                                                                    {pctE !== null
                                                                        ? <PctBadge over={overE}>{pctE.toFixed(1)}%</PctBadge>
                                                                        : <span style={{ color: "#94a3b8" }}>—</span>}
                                                                </TableCell>
                                                                <TableCell align="right"><MetricBadge color="#e67e22">{cfg.cash.toLocaleString("pt-BR")}</MetricBadge></TableCell>
                                                                <TableCell align="right"><MetricBadge color={overC ? "#e74c3c" : "#e67e22"}>{real.cash.toLocaleString("pt-BR")}</MetricBadge></TableCell>
                                                                <TableCell align="center">
                                                                    {pctC !== null
                                                                        ? <PctBadge over={overC}>{pctC.toFixed(1)}%</PctBadge>
                                                                        : <span style={{ color: "#94a3b8" }}>—</span>}
                                                                </TableCell>
                                                                <TableCell align="center">
                                                                    {overE || overC
                                                                        ? <ErrorOutline style={{ color: "#e74c3c", fontSize: 20 }} />
                                                                        : statusOk
                                                                            ? <CheckCircleOutline style={{ color: "#27ae60", fontSize: 20 }} />
                                                                            : <TrendingDown style={{ color: "#94a3b8", fontSize: 20 }} />}
                                                                </TableCell>
                                                            </TableRow>
                                                        );
                                                    })}
                                                </tbody>
                                            </DataTable>
                                        </TableWrapper>
                                        {volBaseRows.length > volBaseRowsPerPage && (
                                            <PaginationContainer>
                                                <PaginationInfo>
                                                    Mostrando {Math.min(((volBasePage - 1) * volBaseRowsPerPage) + 1, volBaseRows.length)}–{Math.min(volBasePage * volBaseRowsPerPage, volBaseRows.length)} de {volBaseRows.length} bases
                                                </PaginationInfo>
                                                <PaginationControls>
                                                    <PaginationLabel>Linhas por página:</PaginationLabel>
                                                    <PaginationSelect value={volBaseRowsPerPage} onChange={(e) => { setVolBaseRowsPerPage(Number(e.target.value)); setVolBasePage(1); }}>
                                                        <option value={5}>5</option>
                                                        <option value={10}>10</option>
                                                        <option value={25}>25</option>
                                                        <option value={50}>50</option>
                                                    </PaginationSelect>
                                                    <PaginationButton disabled={volBasePage === 1} onClick={() => setVolBasePage(p => p - 1)}>Anterior</PaginationButton>
                                                    <PaginationPageInfo>Página {volBasePage} de {volBaseTotalPages}</PaginationPageInfo>
                                                    <PaginationButton disabled={volBasePage >= volBaseTotalPages} onClick={() => setVolBasePage(p => p + 1)}>Próxima</PaginationButton>
                                                </PaginationControls>
                                            </PaginationContainer>
                                        )}
                                    </TableCard>
                                )}

                                <TableCard>
                                    <TableTitleRow>
                                        <TableTitle style={{ margin: 0 }}>Detalhamento Mensal{selectedYear ? ` - ${selectedYear}` : ''}</TableTitle>
                                        <ExportBtn onClick={exportCSV}>⬇ Exportar CSV</ExportBtn>
                                    </TableTitleRow>
                                    <TableWrapper>
                                        <DataTable>
                                            <thead>
                                                <tr>
                                                    <TableHeader>Base</TableHeader>
                                                    <TableHeader>Tipo</TableHeader>
                                                    <TableHeader>Moeda</TableHeader>
                                                    <TableHeader>Mês</TableHeader>
                                                    <TableHeader align="right">Documentos</TableHeader>
                                                    <TableHeader align="right">Valor Total</TableHeader>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {paginatedMetrics.map((metric, index) => {
                                                        const monthNames = ["Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
                                                                           "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"];
                                                        const monthIndex = metric.month - 1;
                                                        const monthName = monthNames[monthIndex] || `Mês ${metric.month}`;
                                                        const tipoLabel = metric.tipoDocument === 'expense' ? 'Expense' :
                                                                         metric.tipoDocument === 'cash' ? 'Cash' : metric.tipoDocument;
                                                        const curr = metric.currency || 'BRL';

                                                        return (
                                                            <TableRow key={`${metric.id || index}`}>
                                                                <TableCell><strong>{metric.database || '-'}</strong></TableCell>
                                                                <TableCell>
                                                                    <TypeBadge type={metric.tipoDocument}>
                                                                        {tipoLabel}
                                                                    </TypeBadge>
                                                                </TableCell>
                                                                <TableCell>
                                                                    <CurrencyBadge>{curr}</CurrencyBadge>
                                                                </TableCell>
                                                                <TableCell><strong>{monthName}</strong></TableCell>
                                                                <TableCell align="right">
                                                                    <MetricBadge color="#8e44ad">
                                                                        {(metric.documentCount || 0).toLocaleString('pt-BR')}
                                                                    </MetricBadge>
                                                                </TableCell>
                                                                <TableCell align="right">
                                                                    <MetricBadge color="#27ae60">
                                                                        {formatCurrency(metric.totalAmount, curr)}
                                                                    </MetricBadge>
                                                                </TableCell>
                                                            </TableRow>
                                                        );
                                                    })}
                                            </tbody>
                                        </DataTable>
                                    </TableWrapper>
                                    <PaginationContainer>
                                        <PaginationInfo>
                                            Mostrando {Math.min(((currentPage - 1) * rowsPerPage) + 1, sortedMetrics.length)}-{Math.min(currentPage * rowsPerPage, sortedMetrics.length)} de {sortedMetrics.length} registros
                                        </PaginationInfo>
                                        <PaginationControls>
                                            <PaginationLabel>Linhas por página:</PaginationLabel>
                                            <PaginationSelect value={rowsPerPage} onChange={(e) => { setRowsPerPage(Number(e.target.value)); setCurrentPage(1); }}>
                                                <option value={5}>5</option>
                                                <option value={10}>10</option>
                                                <option value={25}>25</option>
                                                <option value={50}>50</option>
                                            </PaginationSelect>
                                            <PaginationButton disabled={currentPage === 1} onClick={() => setCurrentPage(p => p - 1)}>Anterior</PaginationButton>
                                            <PaginationPageInfo>Página {currentPage} de {totalPages}</PaginationPageInfo>
                                            <PaginationButton disabled={currentPage >= totalPages} onClick={() => setCurrentPage(p => p + 1)}>Próxima</PaginationButton>
                                        </PaginationControls>
                                    </PaginationContainer>
                                </TableCard>
                            </>
                        ) : (
                            <EmptyStateCard>
                                <EmptyIcon>
                                    <Assessment style={{ fontSize: '64px', color: '#cbd5e1' }} />
                                </EmptyIcon>
                                <EmptyTitle>Nenhum dado encontrado</EmptyTitle>
                                <EmptyText>
                                    Não há métricas para <strong>{selectedConcurName}</strong>{selectedYear ? ` em ${selectedYear}` : ''}
                                </EmptyText>
                            </EmptyStateCard>
                        )}
                    </>
                )}

                {!selectedConcur && (
                    <EmptyStateContent>
                        <EmptyIcon>
                            <Assessment style={{ fontSize: '64px', color: '#cbd5e1' }} />
                        </EmptyIcon>
                        <EmptyTitle>Selecione um Concur</EmptyTitle>
                        <EmptyText>
                            Escolha um Concur e um ano para visualizar as métricas de despesas
                        </EmptyText>
                    </EmptyStateContent>
                )}
            </ResultsCard>
        </Container>
    );
}

export default DashboardConcur;

// ===== STYLED COMPONENTS =====

const FiltersCard = styled.div`
    margin-bottom: 24px;
    padding-top: 24px;
`;

const FilterTitle = styled.h3`
    font-size: 18px;
    font-weight: 700;
    color: #2c3e50;
    margin: 0 0 20px 0;
    font-family: 'Mulish', sans-serif;
`;

const FiltersContainer = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: 20px;
`;

const FilterGroup = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const FilterLabel = styled.label`
    font-size: 14px;
    font-weight: 600;
    color: #4a5568;
    font-family: 'Mulish', sans-serif;
`;

const FilterSelect = styled.select`
    padding: 12px 16px;
    border: 1px solid #e5e7eb;
    border-radius: 10px;
    font-size: 14px;
    font-family: 'Mulish', sans-serif;
    background: white;
    cursor: pointer;
    transition: all 0.2s ease;

    &:focus {
        outline: none;
        border-color: #8e44ad;
        box-shadow: 0 0 0 3px rgba(142, 68, 173, 0.1);
    }
`;

const TotalizersGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
    gap: 20px;
    margin-bottom: 24px;
`;

const fadeIn = keyframes`
    from {
        opacity: 0;
        transform: translateY(10px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
`;

const TotalizerCard = styled.div`
    background: white;
    border-radius: 16px;
    padding: 24px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
    border: 1px solid #e8e8e8;
    display: flex;
    align-items: center;
    gap: 16px;
    animation: ${fadeIn} 0.5s ease;
    transition: all 0.3s ease;

    &:hover {
        transform: translateY(-4px);
        box-shadow: 0 8px 20px rgba(0, 0, 0, 0.1);
    }
`;

const TotalizerIcon = styled.div`
    width: 56px;
    height: 56px;
    border-radius: 12px;
    background: ${props => props.color || '#8e44ad'}15;
    color: ${props => props.color || '#8e44ad'};
    display: flex;
    align-items: center;
    justify-content: center;

    svg {
        font-size: 28px;
    }
`;

const TotalizerContent = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
`;

const TotalizerValue = styled.div`
    font-size: 24px;
    font-weight: 700;
    color: #2c3e50;
    font-family: 'Mulish', sans-serif;
`;

const TotalizerLabel = styled.div`
    font-size: 13px;
    font-weight: 500;
    color: #6b7280;
    font-family: 'Mulish', sans-serif;
`;

const VariationBadge = styled.span`
    display: inline-block;
    margin-top: 6px;
    padding: 3px 9px;
    border-radius: 20px;
    font-size: 11px;
    font-weight: 700;
    font-family: 'Mulish', sans-serif;
    background: ${props => props.positive ? '#dcfce7' : '#fee2e2'};
    color: ${props => props.positive ? '#15803d' : '#dc2626'};
`;

const ChartHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 16px;
`;

const ChartTitle = styled.h4`
    font-size: 15px;
    font-weight: 700;
    color: #2c3e50;
    margin: 0;
    font-family: 'Mulish', sans-serif;
`;

const ToggleGroup = styled.div`
    display: flex;
    gap: 4px;
`;

const ToggleBtn = styled.button`
    padding: 6px 14px;
    border-radius: 8px;
    font-size: 12px;
    font-weight: 600;
    border: 2px solid ${(props) => (props.active ? (props.color || '#8e44ad') : '#e5e7eb')};
    background: ${(props) => (props.active ? (props.color || '#8e44ad') : 'white')};
    color: ${(props) => (props.active ? 'white' : '#9ca3af')};
    cursor: pointer;
    transition: all 0.2s ease;
    font-family: 'Mulish', sans-serif;
`;

const TableTitleRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 20px;
`;

const ExportBtn = styled.button`
    padding: 8px 16px;
    border-radius: 10px;
    font-size: 13px;
    font-weight: 600;
    border: none;
    background: linear-gradient(135deg, #27ae60 0%, #219a52 100%);
    color: white;
    cursor: pointer;
    transition: all 0.2s ease;
    font-family: 'Mulish', sans-serif;
    box-shadow: 0 2px 8px rgba(39, 174, 96, 0.3);

    &:hover {
        transform: translateY(-1px);
        box-shadow: 0 4px 12px rgba(39, 174, 96, 0.4);
    }
`;

const ChartsRow = styled.div`
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 24px;
    margin-bottom: 24px;

    @media (max-width: 1024px) {
        grid-template-columns: 1fr;
    }
`;

const ChartCard = styled.div`
    background: white;
    border-radius: 16px;
    padding: 24px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
    border: 1px solid #e8e8e8;
    animation: ${fadeIn} 0.5s ease;
`;

const FullWidthChartCard = styled(ChartCard)`
    width: 100%;
    margin-bottom: 24px;
`;

const TableCard = styled.div`
    background: white;
    border-radius: 16px;
    padding: 24px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
    border: 1px solid #e8e8e8;
    animation: ${fadeIn} 0.5s ease;
`;

const TableTitle = styled.h3`
    font-size: 18px;
    font-weight: 700;
    color: #2c3e50;
    margin: 0 0 20px 0;
    font-family: 'Mulish', sans-serif;
`;

const TableWrapper = styled.div`
    overflow-x: auto;
`;

const DataTable = styled.table`
    width: 100%;
    border-collapse: collapse;
`;

const TableHeader = styled.th`
    text-align: ${props => props.align || 'left'};
    padding: 12px 16px;
    font-size: 13px;
    font-weight: 600;
    color: #6b7280;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    border-bottom: 2px solid #e5e7eb;
    font-family: 'Mulish', sans-serif;
`;

const TableRow = styled.tr`
    border-bottom: 1px solid #e5e7eb;
    transition: background 0.2s ease;

    &:last-child {
        border-bottom: none;
    }

    &:hover {
        background: #f8f9fa;
    }
`;

const TableCell = styled.td`
    text-align: ${props => props.align || 'left'};
    padding: 16px;
    font-size: 14px;
    color: #2c3e50;
    font-family: 'Mulish', sans-serif;
`;

const MetricBadge = styled.span`
    display: inline-block;
    padding: 6px 12px;
    border-radius: 6px;
    font-size: 13px;
    font-weight: 600;
    background: ${props => props.color || '#8e44ad'}15;
    color: ${props => props.color || '#8e44ad'};
`;

const TypeBadge = styled.span`
    display: inline-block;
    padding: 6px 12px;
    border-radius: 6px;
    font-size: 12px;
    font-weight: 600;
    background: ${props => props.type === 'expense' ? '#8e44ad15' : '#3498db15'};
    color: ${props => props.type === 'expense' ? '#8e44ad' : '#3498db'};
`;

const EmptyStateCard = styled.div`
    background: white;
    border-radius: 16px;
    padding: 64px 24px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
    border: 1px solid #e8e8e8;
    text-align: center;
    animation: ${fadeIn} 0.5s ease;
`;

const EmptyStateContent = styled.div`
    text-align: center;
    padding: 64px 24px;
`;

const EmptyIcon = styled.div`
    margin-bottom: 24px;
`;

const EmptyTitle = styled.h3`
    font-size: 20px;
    font-weight: 700;
    color: #2c3e50;
    margin: 0 0 12px 0;
    font-family: 'Mulish', sans-serif;
`;

const EmptyText = styled.p`
    font-size: 14px;
    color: #6b7280;
    margin: 0;
    font-family: 'Mulish', sans-serif;
`;

const PaginationContainer = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px 0 0;
    margin-top: 16px;
    border-top: 1px solid #e5e7eb;
    flex-wrap: wrap;
    gap: 12px;
`;

const PaginationInfo = styled.span`
    font-size: 13px;
    color: #6b7280;
    font-family: 'Mulish', sans-serif;
`;

const PaginationControls = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
`;

const PaginationLabel = styled.span`
    font-size: 13px;
    color: #6b7280;
    font-family: 'Mulish', sans-serif;
`;

const PaginationSelect = styled.select`
    padding: 6px 10px;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    font-size: 13px;
    font-family: 'Mulish', sans-serif;
    background: white;
    cursor: pointer;

    &:focus {
        outline: none;
        border-color: #8e44ad;
        box-shadow: 0 0 0 2px rgba(142, 68, 173, 0.1);
    }
`;

const PaginationButton = styled.button`
    padding: 8px 16px;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    font-size: 13px;
    font-family: 'Mulish', sans-serif;
    background: white;
    color: #2c3e50;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover:not(:disabled) {
        background: #f3f4f6;
        border-color: #8e44ad;
        color: #8e44ad;
    }

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
`;

const PaginationPageInfo = styled.span`
    font-size: 13px;
    color: #2c3e50;
    font-weight: 600;
    font-family: 'Mulish', sans-serif;
`;

const CurrencyBadge = styled.span`
    display: inline-block;
    padding: 3px 10px;
    border-radius: 20px;
    font-size: 11px;
    font-weight: 700;
    font-family: 'Mulish', sans-serif;
    background: #8e44ad20;
    color: #8e44ad;
    letter-spacing: 0.5px;
`;

const CurrencyKPICard = styled.div`
    background: white;
    border-radius: 16px;
    padding: 24px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
    border: 1px solid #e8e8e8;
    animation: ${fadeIn} 0.5s ease;
    transition: all 0.3s ease;

    &:hover {
        transform: translateY(-4px);
        box-shadow: 0 8px 20px rgba(0, 0, 0, 0.1);
    }
`;

const CurrencyKPIHeader = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 12px;
    font-size: 13px;
    font-weight: 600;
    color: #6b7280;
    font-family: 'Mulish', sans-serif;
`;

const CurrencyKPIValue = styled.div`
    font-size: 22px;
    font-weight: 700;
    color: #2c3e50;
    font-family: 'Mulish', sans-serif;
    margin-bottom: 4px;
`;

const CurrencyKPIDetail = styled.div`
    font-size: 12px;
    color: #9ca3af;
    font-family: 'Mulish', sans-serif;
`;

// ── Volumetria styled components ──────────────────────────────

const VolSection = styled.div`
    background: #faf7ff;
    border: 1.5px solid #e9d5ff;
    border-radius: 16px;
    padding: 24px;
    margin-bottom: 24px;
    animation: ${fadeIn} 0.4s ease;
`;

const VolSectionHeader = styled.div`
    margin-bottom: 20px;
    padding-bottom: 16px;
    border-bottom: 2px solid #e9d5ff;
`;

const VolSectionTitle = styled.h3`
    font-size: 17px;
    font-weight: 800;
    font-family: 'Mulish', sans-serif;
    color: #2c3e50;
    margin: 0;
    display: flex;
    align-items: center;
`;

const VolKpiRow = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
    gap: 16px;
    margin-bottom: 20px;
`;

const VolKpiCard = styled.div`
    background: white;
    border-radius: 14px;
    padding: 18px 20px;
    border: 1.5px solid ${p => p.statusColor || '#e2e8f0'};
    border-top: 4px solid ${p => p.statusColor || '#8e44ad'};
    box-shadow: 0 2px 8px rgba(0,0,0,0.05);
`;

const VolKpiTop = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 12px;
`;

const VolKpiLabel = styled.div`
    font-size: 13px;
    font-weight: 700;
    font-family: 'Mulish', sans-serif;
    color: #64748b;
    text-transform: uppercase;
    letter-spacing: 0.06em;
`;

const VolKpiBadge = styled.div`
    font-size: 14px;
    font-weight: 800;
    font-family: 'Mulish', sans-serif;
    color: ${p => p.statusColor || '#8e44ad'};
    background: ${p => p.statusColor ? `${p.statusColor}15` : '#8e44ad15'};
    padding: 3px 10px;
    border-radius: 20px;
`;

const VolKpiNumbers = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 12px;
`;

const VolKpiItem = styled.div`
    flex: 1;
    text-align: center;
`;

const VolKpiDivider = styled.div`
    width: 1px;
    height: 36px;
    background: #e2e8f0;
    flex-shrink: 0;
`;

const VolKpiNum = styled.div`
    font-size: 22px;
    font-weight: 800;
    font-family: 'Mulish', sans-serif;
    color: ${p => p.color || '#2c3e50'};
`;

const VolKpiSub = styled.div`
    font-size: 11px;
    font-weight: 600;
    color: #94a3b8;
    font-family: 'Mulish', sans-serif;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    margin-top: 2px;
`;

const VolProgressBar = styled.div`
    height: 6px;
    background: #f0f0f0;
    border-radius: 99px;
    overflow: hidden;
`;

const VolProgressFill = styled.div`
    height: 100%;
    width: ${p => Math.min(p.pct || 0, 100)}%;
    background: ${p => p.color || '#8e44ad'};
    border-radius: 99px;
    transition: width 0.8s ease;
`;

const PctBadge = styled.span`
    display: inline-block;
    padding: 3px 10px;
    border-radius: 20px;
    font-size: 12px;
    font-weight: 700;
    font-family: 'Mulish', sans-serif;
    background: ${p => p.over ? 'rgba(231,76,60,0.12)' : 'rgba(39,174,96,0.12)'};
    color: ${p => p.over ? '#e74c3c' : '#27ae60'};
`;
